import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { applicationRouteSuffixForTemplate } from 'tio-employee/utils/tas/application-flow';
import { findRecord } from '@warp-drive/utilities/json-api';
import type RouterService from '@ember/routing/router-service';
import type { Store } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type FeaturesService from 'tio-common/services/features';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import {
  dynamicEligibilityRequest,
  periodsOfEligibilityRequest,
} from 'tio-data/requests/tas-participant-actions';
import { loadTasApplicationHistory } from 'tio-data/requests/tas-application-actions';
import type { DynamicEligibility, PeriodOfEligibility } from 'tio-common/types/tuition-assistance';

interface TasApplicationShowRoute {
  application: TasApplicationModel;
  dynamicEligibility: boolean;
  programEligibility: string[];
}

export default class AuthenticatedTasApplicationsShowRoute extends Route {
  @service declare features: FeaturesService;
  @service declare router: RouterService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;

  includes = [
    'tas-courses',
    'tas-courses.tas-assets.attachments',
    'tas-program-instance',
    'tas-program-instance.tas-program-template',
    'tas-program-instance.tas-program-template.conditions.public-assets',
    'tas-program-instance.tas-program-template.tas-field-options',
    'tas-program-instance.tas-program-template.company.company-setting',
    'tas-program-instance.employee.person',
    'tas-program-instance.employee.tas-participant',
    'tas-assets.attachments',
    'tas-assistances',
    'tas-assistances.attachments',
    'tas-assistances.tas-assets',
    'tas-assistances.tas-assets.attachments',
    'tags',
    'comments',
    'pcs-cards.pcs-card-holder',
    'pcs-cards.pcs-company',
    'pcs-cards.pcs-authorizations',
    'pcs-cards.pcs-card-events',
    'pcs-cards.pcs-transactions',
  ];

  queryParams = {
    applicationSubmitted: { refreshModel: true },
  };

  async model({ tas_application_id }: { tas_application_id: string }) {
    // WarpDrive builder read (Phase 2.4) — replaces store.findRecord; reload: true maps to builder option.
    const { content: applicationContent } = (await this.store.request(
      findRecord<TasApplicationModel>('tas-application', tas_application_id, {
        include: this.includes.join(','),
        reload: true,
      })
    )) as { content: { data: TasApplicationModel } };
    const application = applicationContent.data;

    await this.tuitionAssistance.loadEligibilitiesForCurrentEmployee();

    await loadTasApplicationHistory(this.store, application);

    const id = application.tasProgramInstance.employee.tasParticipant.id as string;
    const { code } = application.tasProgramInstance.tasProgramTemplate;

    const [dynamicResponse, periodsResponse] = await Promise.all([
      this.store.request(dynamicEligibilityRequest(id)),
      this.store.request(periodsOfEligibilityRequest(id)),
    ]);
    // Legacy adapter filtered the per-program-code value off the raw response
    // (`programCode ? all[code] : all`); parity keeps the bare lookup.
    const dynamicEligibility = (dynamicResponse.content as DynamicEligibility)[code] as boolean;
    const programEligibility = (periodsResponse.content as PeriodOfEligibility)[
      code
    ] as unknown as string[];

    return { application, dynamicEligibility, programEligibility };
  }

  afterModel(model: TasApplicationShowRoute) {
    const { state } = model.application;
    const template = model.application.tasProgramInstance.tasProgramTemplate;
    const type = template.typeClassification;
    const suffix = applicationRouteSuffixForTemplate(template);

    // Mapping of types and states to routes
    const routeMappings: Record<string, string> = {
      'TAS.ProgramType.2-TAS.ApplicationState.DEFINE_COURSES': `authenticated.tas.applications.type-two${suffix}.new`,
      'TAS.ProgramType.3-TAS.ApplicationState.DEFINE_COURSES': `authenticated.tas.applications.type-three${suffix}.new`,
      'TAS.ProgramType.4.2-TAS.ApplicationState.DEFINE_COURSES': `authenticated.tas.applications.type-four${suffix}.new`,
      'TAS.ProgramType.4.3-TAS.ApplicationState.DEFINE_COURSES': `authenticated.tas.applications.type-four${suffix}.new`,
    };

    // Create a key from type and state
    const key = `${type}-${state}`;

    // Transition to the appropriate route if it matches, otherwise allow /show route to render
    const route = routeMappings[key];
    if (route) {
      this.router.replaceWith(route, model.application.id);
    }
  }
}
